import * as Api from '@ViewModels';
import * as React from 'react';
import { useScheduledMeetingFilter } from '../../../../queries';
import { IDateRange } from '../utils/dateRange';

/** Fetch a generous page so the whole visible window usually lands in one request. */
const CALENDAR_PAGE_SIZE = 100;

/**
 * The ScheduledMeeting filter only supports a `StartDate` bound (no `EndDate`/overlap),
 * so a multi-day meeting that started before the window wouldn't be returned. We extend
 * the lower bound back by this many days to catch meetings that began earlier but overlap
 * the window; anything that doesn't actually overlap just isn't rendered. Meetings longer
 * than this look-back are the only ones still missed.
 */
const OVERLAP_LOOKBACK_DAYS = 32;
const OVERLAP_LOOKBACK_MS = OVERLAP_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;

/**
 * Safety cap on the eager page-drain. At {@link CALENDAR_PAGE_SIZE} per page this is far
 * more meetings than any single visible window realistically holds; it exists only so a
 * pathological window can't spin through unbounded requests.
 */
const MAX_AUTO_PAGES = 10;

export interface IUseCalendarMeetingsResult {
	meetings: Api.IScheduledMeeting[];
	/** True until the first page resolves. */
	isLoading: boolean;
}

/**
 * Fetch every Levitate scheduled meeting whose start falls inside the visible
 * calendar window. Wraps {@link useScheduledMeetingFilter}, translating the window
 * into `StartDate` Gt/Lt criteria, and transparently pages through all results so
 * callers receive a single flat list. Honors the admin impersonation context.
 */
export function useCalendarMeetings({
	range,
	hostId,
	forUserId,
	extraCriteria,
	impersonationContext,
	enabled = true,
}: {
	range: IDateRange;
	/** When set, restrict results to meetings hosted by this user. */
	hostId?: string;
	forUserId?: string;
	/** Additional filter criteria (e.g. status/search) AND-ed with the date window. */
	extraCriteria?: Api.IScheduledMeetingFilterCriteria[];
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}): IUseCalendarMeetingsResult {
	const filterRequest = React.useMemo<Api.IScheduledMeetingFilterRequest>(() => {
		// `Gt`/`Lt` are exclusive — nudge the lower bound back by 1ms so a meeting
		// landing exactly on the window's opening instant is still included, and by the
		// overlap look-back so multi-day meetings that began earlier still come back.
		const lowerBound = new Date(range.start.getTime() - OVERLAP_LOOKBACK_MS).toISOString();
		const upperBound = range.end.toISOString();
		const criteria: Api.IScheduledMeetingFilterCriteria[] = [
			{ property: 'StartDate', value: lowerBound, op: 'Gt' },
			{ property: 'StartDate', value: upperBound, op: 'Lt' },
		];
		if (hostId) {
			criteria.push({ property: 'Host', value: hostId });
		}
		if (extraCriteria?.length) {
			criteria.push(...extraCriteria);
		}
		return { criteria };
	}, [range.start, range.end, hostId, extraCriteria]);

	const query = useScheduledMeetingFilter({
		filterRequest,
		pageSize: CALENDAR_PAGE_SIZE,
		sortBy: 'published.startDate',
		sort: 'asc',
		expand: 'participants',
		forUserId,
		impersonationContext,
		enabled,
		keepPreviousData: true,
		toastErrorMessage: 'Failed to load meetings',
	});

	const { hasNextPage, isFetchingNextPage, fetchNextPage, isFetchNextPageError } = query;
	const pagesLoaded = query.data?.pages.length ?? 0;

	// A failed page leaves `hasNextPage` true and `pagesLoaded` unchanged, so without the
	// `isFetchNextPageError` guard this effect re-fires and retries that page indefinitely.
	React.useEffect(() => {
		if (enabled && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && pagesLoaded < MAX_AUTO_PAGES) {
			fetchNextPage();
		}
	}, [enabled, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pagesLoaded]);

	const meetings = React.useMemo(() => query.data?.pages.flatMap(page => page.values) ?? [], [query.data?.pages]);

	return {
		meetings,
		isLoading: enabled ? query.isPending : false,
	};
}
